import {useEffect, useMemo, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router';
import PageShell from '../components/PageShell';
import StripeCheckout from '../components/StripeCheckout';
import {getClinics, getDivisions, getDoctors, getClinicDivisionTimeSlots} from '../api/ClinicApi';
import {
  createReservation,
  createPayment,
  confirmPayment,
  deleteReservation,
  getForm,
  getReservation,
  submitAnswer,
} from '../api/ReservationApi';
import {problemMessage} from '../api/client';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';
import {
  PARTIAL_COPY,
  RELEASED_COPY,
  STEPS,
  canCancelAt,
  isReleased,
  isReleasedConflict,
  needsPayment,
} from '../consultation/bookingState';
import {recordPath} from '../consultation/deepLinks';

const localTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const PROCESSING = 'Payment is still processing. Please check again shortly.';

const formatSlot = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const messageOf = (err) => problemMessage(err, 'Something went wrong. Please try again.');

/**
 * Virtual-consultation booking wizard: division -> doctor -> time slot -> intake questions
 * -> reservation -> payment (or doctor approval). Replaces the legacy server-rendered pages.
 *
 * Once the reservation exists, the remaining steps (intake answers, payment creation, payment
 * confirmation) can each fail without losing it. The wizard then says which step stopped, and
 * Retry resumes that step under the same Idempotency-Key, so a request the server committed but
 * whose answer was dropped is replayed instead of duplicated.
 */
export default function ConsultationBook() {
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [needsApproval, setNeedsApproval] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Where the booking stopped after the reservation: {step, message, id}. `id` changes on every
  // failure, so the message is announced and focus moves to its heading each time.
  const [failed, setFailed] = useState(null);
  const [pendingAnswers, setPendingAnswers] = useState([]);
  const failureSeq = useRef(0);

  useEffect(() => {
    getClinics()
      .then(async (clinics) => {
        const lists = await Promise.all(clinics.map((c) => getDivisions(c.id)));
        setDivisions(lists.flat());
      })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    setDoctorId('');
    setSlot(null);
    if (!divisionId) return;
    getDoctors(divisionId)
      .then(setDoctors)
      .catch((err) => setError(err.message));
  }, [divisionId]);

  const doctor = useMemo(() => doctors.find((d) => d.id === doctorId), [doctors, doctorId]);
  const assignment = useMemo(
    () => (doctor ? doctor.divisions.find((d) => d.division_id === divisionId) : null),
    [doctor, divisionId],
  );

  useEffect(() => {
    setSlot(null);
    setSlots([]);
    setForm(null);
    if (!doctorId || !divisionId) return;
    getClinicDivisionTimeSlots(null, divisionId, {doctorId, timezone: localTz()})
      .then((res) => {
        const mine = (res.doctors || []).find((d) => d.doctor_id === doctorId);
        setSlots(mine ? mine.free_slots : []);
      })
      .catch((err) => setError(err.message));
  }, [doctorId, divisionId]);

  useEffect(() => {
    if (!assignment) return;
    getForm(assignment.form_id)
      .then(setForm)
      .catch(() => setForm(null));
  }, [assignment]);

  const missingAnswers = form
    ? form.questions.filter((q) => q.is_required && !(answers[q.form_and_question_id] || '').trim())
    : [];

  const answeredIds = () =>
    Object.entries(answers)
      .filter(([, v]) => v && v.trim())
      .map(([qid]) => qid);

  const stopAt = (step, err) => {
    failureSeq.current += 1;
    setFailed({step, message: messageOf(err), id: failureSeq.current});
  };

  /**
   * Runs the open steps for reservation `r` in order: intake answers (the ids in `qids`), then
   * payment creation when the consultation is paid for. Stops at the first failure, keeping the
   * reservation. Every request keeps its own key, so a retry of a failed request replays it.
   */
  const advance = async (r, qids) => {
    setBusy(true);
    try {
      if (qids.length) {
        const results = await Promise.allSettled(
          qids.map((qid) => {
            const answer = answers[qid].trim();
            return keys.run(`answer:${qid}`, {reservation: r.id, answer}, (idempotencyKey) =>
              submitAnswer(r.id, qid, answer, {idempotencyKey}),
            );
          }),
        );
        const unsaved = qids.filter((_, i) => results[i].status === 'rejected');
        setPendingAnswers(unsaved);
        if (unsaved.length) {
          const reason = results.find((x) => x.status === 'rejected').reason;
          stopAt(STEPS.answers, reason);
          return;
        }
      }
      if (needsPayment(r) && !payment) {
        try {
          const p = await keys.run('payment', {reservation: r.id}, (idempotencyKey) =>
            createPayment(r.id, {idempotencyKey}),
          );
          setPayment(p);
        } catch (err) {
          stopAt(STEPS.payment, err);
          return;
        }
      }
      setFailed(null);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    let r;
    try {
      // Each request of the booking keeps its own key: a retry of a failed request replays it.
      const booking = {
        doctor_id: doctorId,
        division_id: divisionId,
        reservation_time: slot.start_time,
        end_time: slot.end_time,
        timezone: localTz(),
        needs_approval: needsApproval,
      };
      r = await keys.run('reservation', booking, (idempotencyKey) =>
        createReservation(booking, {idempotencyKey}),
      );
    } catch (err) {
      setError(
        err.response && err.response.status === 409
          ? 'That time was just taken. Please pick another slot.'
          : err.message,
      );
      setBusy(false);
      return;
    }
    // The reservation now exists: from here on the wizard never restarts the booking.
    setReservation(r);
    const qids = answeredIds();
    setPendingAnswers(qids);
    await advance(r, qids);
  };

  /** Retry of the step that stopped: answers and payment creation re-run, confirmation re-checks. */
  const retry = () => {
    if (failed.step === STEPS.confirm) return retryConfirm();
    return advance(reservation, pendingAnswers);
  };

  /** Confirms the Stripe payment with aidb. Throws when aidb does not report it paid. */
  const confirmPaid = async (r) => {
    const status = await keys.run('confirm-payment', {reservation: r.id}, (idempotencyKey) =>
      confirmPayment(r.id, {idempotencyKey}),
    );
    if (!status.paid) throw new Error(PROCESSING);
  };

  // Stripe has taken the card and calls this. A dropped confirmation is a failed step (Retry
  // confirms again, the card is not charged twice). "Still processing" stays on the card form,
  // where Pay can be tried again.
  const onPaid = async () => {
    try {
      await confirmPaid(reservation);
      setFailed(null);
      setPaid(true);
    } catch (err) {
      if (err.message === PROCESSING) throw err;
      if (isReleasedConflict(err)) return releaseBooking(reservation);
      stopAt(STEPS.confirm, err);
    }
  };

  // Confirmation answered 409: the consultation was released before the payment arrived and the
  // charge is refunded. That ends the wizard with the released message, not a Retry. The local
  // copy is marked at once, then the server state is re-read.
  const releaseBooking = (r) => {
    setFailed(null);
    setReservation({...r, status: 'released', is_cancelled: true});
    getReservation(r.id)
      .then(setReservation)
      .catch(() => {});
  };

  const retryConfirm = async () => {
    setBusy(true);
    try {
      await confirmPaid(reservation);
      setFailed(null);
      setPaid(true);
    } catch (err) {
      if (isReleasedConflict(err)) releaseBooking(reservation);
      else stopAt(STEPS.confirm, err);
    } finally {
      setBusy(false);
    }
  };

  const cancelBooking = async () => {
    setBusy(true);
    setCancelling(true);
    try {
      await keys.run('cancel', {reservation: reservation.id}, (idempotencyKey) =>
        deleteReservation(reservation.id, {idempotencyKey}),
      );
      setFailed(null);
      setCancelled(true);
    } catch (err) {
      stopAt(failed.step, err);
    } finally {
      setCancelling(false);
      setBusy(false);
    }
  };

  // Heading focus on mount: each new state (or each new failure) moves focus to its heading.
  const focusOnMount = (el) => {
    if (el) el.focus();
  };

  if (reservation) {
    const booked = paid || (reservation.status === 'confirmed' && !needsPayment(reservation));
    const released = isReleased(reservation);
    return (
      <PageShell title="Book a consultation" showBackButton={true}>
        <div className="container py-3" style={{maxWidth: 560}}>
          {cancelled && (
            <div role="status">
              <h2 ref={focusOnMount} tabIndex={-1}>
                Reservation cancelled
              </h2>
              <p>No payment was taken, so nothing needs refunding.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/virtual-consultation/reservation-record')}
              >
                View my consultations
              </button>
            </div>
          )}
          {!cancelled && failed && (
            <div role="alert" key={failed.id}>
              <h2 ref={focusOnMount} tabIndex={-1}>
                {PARTIAL_COPY[failed.step].heading}
              </h2>
              <p>{PARTIAL_COPY[failed.step].body}</p>
              <p className="text-danger" style={{whiteSpace: 'pre-line'}}>
                {failed.message}
              </p>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <button type="button" className="btn btn-primary" onClick={retry} disabled={busy}>
                  {busy ? (cancelling ? 'Cancelling…' : 'Retrying…') : 'Retry'}
                </button>
                {canCancelAt(failed.step) && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={cancelBooking}
                    disabled={busy}
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel reservation'}
                  </button>
                )}
              </div>
              <p>
                <Link to={recordPath(reservation.id)}>View this consultation</Link>
              </p>
            </div>
          )}
          {!cancelled && !failed && busy && !payment && <p role="status">Finishing your booking…</p>}
          {!cancelled && released && (
            <div role="alert">
              <h2 ref={focusOnMount} tabIndex={-1}>
                {RELEASED_COPY.heading}
              </h2>
              <p>{RELEASED_COPY.body}</p>
              <p>
                <Link to={recordPath(reservation.id)}>View this consultation</Link>
              </p>
            </div>
          )}
          {!cancelled && !failed && !busy && reservation.status === 'pending' && (
            <div role="status">
              <h2>Request sent</h2>
              <p>
                Dr. {reservation.doctor_name} will review your request. We will email you once it is approved;
                it expires automatically if not answered within 24 hours.
              </p>
            </div>
          )}
          {!cancelled && !released && payment && !paid && (
            // Stays mounted (hidden) while a failed confirmation is shown, so Stripe's own
            // pending call can finish without updating an unmounted form.
            <div hidden={Boolean(failed)}>
              <h2>Payment</h2>
              <StripeCheckout
                clientSecret={payment.client_secret}
                publishableKey={payment.publishable_key}
                amountCents={payment.amount_cents}
                onPaid={onPaid}
              />
            </div>
          )}
          {!cancelled && !failed && !busy && booked && (
            <div role="status">
              <h2 ref={focusOnMount} tabIndex={-1}>
                You are booked!
              </h2>
              <p>{formatSlot(reservation.reservation_time)}</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/virtual-consultation/reservation-record')}
              >
                View my consultations
              </button>
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Book a consultation" showBackButton={true}>
      <div className="container py-3" style={{maxWidth: 560}}>
        <label htmlFor="division" className="form-label">
          Specialty
        </label>
        <select
          id="division"
          className="form-select mb-3"
          value={divisionId}
          onChange={(e) => setDivisionId(e.target.value)}
        >
          <option value="">Choose a specialty</option>
          {divisions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.display_name}
            </option>
          ))}
        </select>

        <label htmlFor="doctor" className="form-label">
          Dentist
        </label>
        <select
          id="doctor"
          className="form-select mb-3"
          value={doctorId}
          disabled={!divisionId}
          onChange={(e) => setDoctorId(e.target.value)}
        >
          <option value="">Choose a dentist</option>
          {doctors.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        {doctorId && (
          <fieldset className="mb-3">
            <legend className="form-label fs-6">Available times</legend>
            {slots.length === 0 && <p>No open times in the next 30 days.</p>}
            <div className="d-flex flex-wrap gap-2" role="radiogroup" aria-label="Available times">
              {slots.slice(0, 40).map((s) => (
                <button
                  key={s.start_time}
                  type="button"
                  role="radio"
                  aria-checked={slot === s}
                  className={`btn btn-sm ${slot === s ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setSlot(s)}
                >
                  {formatSlot(s.start_time)}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {slot && form && (
          <fieldset className="mb-3">
            <legend className="form-label fs-6">{form.title}</legend>
            {form.questions.map((q) => (
              <div key={q.form_and_question_id} className="mb-2">
                <label htmlFor={`q-${q.form_and_question_id}`} className="form-label">
                  {q.question_label}
                  {q.is_required && ' *'}
                </label>
                <textarea
                  id={`q-${q.form_and_question_id}`}
                  className="form-control"
                  rows={2}
                  value={answers[q.form_and_question_id] || ''}
                  onChange={(e) => setAnswers((a) => ({...a, [q.form_and_question_id]: e.target.value}))}
                />
              </div>
            ))}
          </fieldset>
        )}

        {slot && (
          <div className="form-check mb-3">
            <input
              id="needs-approval"
              className="form-check-input"
              type="checkbox"
              checked={needsApproval}
              onChange={(e) => setNeedsApproval(e.target.checked)}
            />
            <label htmlFor="needs-approval" className="form-check-label">
              Ask the dentist to approve first
            </label>
          </div>
        )}

        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        <button
          type="button"
          className="btn btn-primary w-100"
          disabled={!slot || busy || missingAnswers.length > 0}
          onClick={submit}
        >
          {busy ? 'Booking…' : assignment ? `Book for $${assignment.charge.toFixed(2)}` : 'Book'}
        </button>
      </div>
    </PageShell>
  );
}
